import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { APP_VERSION } from '../generated/release.js'
import { useI18n } from '../i18n/context.js'
import { paths } from '../routes.js'
import { createReleaseSync } from '../shell/releaseSync.js'

interface AvailableRelease {
  version: string
  locale: 'it' | 'en'
  summary: string
  areas: Array<{ area: string; items: string[] }>
}

function normaliseRelease(value: unknown, version: string, locale: 'it' | 'en'): AvailableRelease {
  const release = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  const translations = release.translations && typeof release.translations === 'object' ? release.translations as Record<string, unknown> : {}
  const translated = locale === 'en' && translations.en && typeof translations.en === 'object' ? translations.en as Record<string, unknown> : null
  const visible = translated ?? release
  const areas = Array.isArray(visible.areas)
    ? visible.areas.flatMap((candidate) => {
        if (!candidate || typeof candidate !== 'object') return []
        const area = candidate as Record<string, unknown>
        const items = Array.isArray(area.items) ? area.items.filter((item): item is string => typeof item === 'string') : []
        return items.length > 0 ? [{ area: typeof area.area === 'string' ? area.area : 'Aggiornamenti', items }] : []
      })
    : []
  const flat = [...(Array.isArray(visible.changes) ? visible.changes : []), ...(Array.isArray(visible.items) ? visible.items : [])]
    .filter((item): item is string => typeof item === 'string')
  return {
    version,
    locale,
    summary: typeof visible.summary === 'string' ? visible.summary : locale === 'en' ? `Version ${version}` : `Versione ${version}`,
    areas: areas.length > 0 ? areas : [{ area: locale === 'en' ? 'Updates' : 'Aggiornamenti', items: flat }],
  }
}

async function loadRelease(version: string, locale: 'it' | 'en'): Promise<AvailableRelease> {
  try {
    const response = await fetch(`/release-notes.json?_=${Date.now()}`, { cache: 'no-store' })
    if (response.ok) {
      const notes = await response.json() as { releases?: unknown[] }
      const match = notes.releases?.find((candidate) => (
        candidate && typeof candidate === 'object' && (candidate as { version?: unknown }).version === version
      ))
      return normaliseRelease(match, version, locale)
    }
  } catch {
    // Without the notes the box still shows the version.
  }
  return normaliseRelease({}, version, locale)
}

/**
 * Automatic update across tabs (guide 30, § 4).
 *
 * /version.json is read on browser events only (start, focus, visibility,
 * online), never on a timer. A new version reloads every open tab by itself,
 * without confirmation; the notes of the new version show up after the reload
 * with a single «Got it» button, and closing them in one tab closes them in all.
 */
export function ReleaseProtocol() {
  const { t, locale } = useI18n()
  const [shown, setShown] = useState<AvailableRelease | null>(null)

  const sync = useMemo(() => createReleaseSync({
    appId: 'cms',
    bundleVersion: APP_VERSION,
    onNotesChange: (notes) => {
      if (!notes) setShown(null)
    },
  }), [])

  useEffect(() => {
    // Removes the cache-buster from the address and the keys of the old mechanisms.
    const url = new URL(window.location.href)
    if (url.searchParams.has('_v')) {
      url.searchParams.delete('_v')
      window.history.replaceState(window.history.state, '', url.toString())
    }
    try {
      window.localStorage.removeItem('cms-release-last-check')
      window.localStorage.removeItem('cms-release-available')
      window.localStorage.removeItem('cms.release.accepted')
    } catch {
      // Storage may be disabled.
    }
    const notes = sync.notes()
    if (notes) void loadRelease(notes.version, locale).then(setShown)
    return sync.start()
  }, [sync])

  useEffect(() => {
    // Language switched while the notes are open: reload them in the new language.
    if (shown && shown.locale !== locale) void loadRelease(shown.version, locale).then(setShown)
  }, [shown, locale])

  const dismiss = () => {
    sync.dismissNotes()
    setShown(null)
  }

  return (
    <>
      <footer className="app-release-bar">
        <span>v{APP_VERSION}</span>
        <Link to={paths.releaseNotes()}>{t('release.notes')}</Link>
      </footer>
      {shown && (
        <div className="dialog-scrim" role="presentation">
          <section className="dialog dialog-md release-dialog" role="dialog" aria-modal="true" aria-labelledby="release-title">
            <header className="dialog-head release-head">
              <p className="release-kicker">{t('release.updated', { version: shown.version })}</p>
              <h2 id="release-title" className="dialog-title">{shown.summary}</h2>
            </header>
            <div className="dialog-body release-scroll">
              {shown.areas.map((area) => (
                <section key={area.area}>
                  <h3>{area.area}</h3>
                  <ul>{area.items.map((item) => <li key={item}>{item}</li>)}</ul>
                </section>
              ))}
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn btn-filled" onClick={dismiss}>{t('release.dismiss')}</button>
            </div>
          </section>
        </div>
      )}
    </>
  )
}
